import { useCallback, useState, type ReactNode } from 'react';
import { Box, Button, Group, Stack, Text, Title } from '@mantine/core';
import { FeedbackOverlay, type FeedbackType } from '@commander/ui';
import { useKiosk } from '@/providers/kioskState';
import { useCheckinByCode, useEquipmentDetail, formatProblemError } from '@commander/api-client';

interface OverlayState {
  visible: boolean;
  type: FeedbackType;
  title: string;
  message: string;
}

const HIDDEN_OVERLAY: OverlayState = {
  visible: false,
  type: 'success',
  title: '',
  message: '',
};

interface CheckinConfirmScreenProps {
  /** Optional override for navigation after the overlay auto-dismisses.
   * Defaults to the kiosk reset action (return to home / idle). */
  onComplete?: () => void;
}

/**
 * CheckinConfirmScreen — operator picks one of two explicit paths:
 *
 *  - Return                — normal check-in, isDefective=false
 *  - Return as defective   — flags the radio for inspection, isDefective=true
 *
 * No auto-confirm timer; the operator must choose. After the mutation,
 * a FeedbackOverlay shows the outcome (success/warning/error) and on
 * auto-dismiss the kiosk resets to its idle screen.
 */
export function CheckinConfirmScreen({ onComplete }: CheckinConfirmScreenProps = {}): ReactNode {
  const { state, reset } = useKiosk();
  const checkinMutation = useCheckinByCode(state.kioskCode);
  const { data: equipment } = useEquipmentDetail(state.equipmentId ?? '');
  const [overlay, setOverlay] = useState<OverlayState>(HIDDEN_OVERLAY);

  const submit = useCallback(
    async (isDefective: boolean) => {
      if (!state.equipmentId || checkinMutation.isPending) return;

      try {
        const result = await checkinMutation.mutateAsync({
          equipmentId: state.equipmentId,
          isDefective,
        });
        setOverlay({
          visible: true,
          type: isDefective ? 'warning' : 'success',
          title: isDefective ? 'Marked defective' : 'Returned',
          message: isDefective
            ? `Radio flagged for inspection. Duration: ${result.duration}.`
            : `Radio returned. Duration: ${result.duration}.`,
        });
      } catch (error) {
        setOverlay({
          visible: true,
          type: 'error',
          title: 'Return failed',
          message: formatProblemError(
            error,
            'The check-in could not be completed. Please try again.',
          ),
        });
      }
    },
    [state.equipmentId, checkinMutation],
  );

  const handleOverlayHide = useCallback(() => {
    setOverlay(HIDDEN_OVERLAY);
    if (overlay.type === 'error') {
      // Error: stay on the confirm screen so the operator can retry or cancel.
      return;
    }
    if (onComplete) {
      onComplete();
    } else {
      reset();
    }
  }, [overlay.type, onComplete, reset]);

  return (
    <Stack align="center" gap={36}>
      <Box
        className="kiosk-glow kiosk-glow--success"
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--cmd-success) 0%, #16A34A 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow:
            '0 8px 32px var(--cmd-success-glow), 0 2px 8px rgba(0, 0, 0, 0.10)',
          transition: 'all var(--duration-normal) var(--ease-out)',
        }}
      >
        <svg
          width="44"
          height="44"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      </Box>

      <Stack align="center" gap={4}>
        <Title
          order={2}
          ta="center"
          fw={700}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-primary)',
            fontSize: '1.5rem',
            letterSpacing: '-0.01em',
          }}
        >
          Confirm return
        </Title>
        <Text
          ta="center"
          fw={500}
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '0.9375rem',
          }}
        >
          Choose how to return this radio
        </Text>
      </Stack>

      <Box
        style={{
          width: '100%',
          maxWidth: 500,
          borderRadius: 'var(--radius-xl)',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
          padding: 'var(--space-8)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <Text
          fw={600}
          mb={8}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-muted)',
            fontSize: '0.6875rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Equipment
        </Text>
        <Text
          fw={600}
          style={{
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)',
            fontSize: '1.25rem',
            letterSpacing: '0.04em',
          }}
        >
          {equipment?.tagId ?? state.equipmentId}
        </Text>
        {equipment?.description && (
          <Text
            mt={4}
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
            }}
          >
            {equipment.description}
          </Text>
        )}
      </Box>

      <Group gap="md" wrap="wrap" style={{ width: '100%', maxWidth: 500 }}>
        <Button
          size="xl"
          h={72}
          flex={1}
          onClick={() => submit(false)}
          loading={checkinMutation.isPending}
          data-testid="kiosk-checkin-return"
          styles={{
            root: {
              fontSize: '1.0625rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-lg)',
              fontFamily: 'var(--font-heading)',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              backgroundColor: 'var(--status-online-accent, var(--green-accent))',
              boxShadow: '0 4px 16px var(--cmd-success-glow)',
              border: 'none',
              color: 'white',
            },
          }}
        >
          Return
        </Button>
        <Button
          size="xl"
          h={72}
          flex={1}
          onClick={() => submit(true)}
          loading={checkinMutation.isPending}
          data-testid="kiosk-checkin-return-defective"
          leftSection={
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          }
          styles={{
            root: {
              fontSize: '1.0625rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-lg)',
              fontFamily: 'var(--font-heading)',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              backgroundColor: 'var(--status-warning-accent, var(--amber-accent))',
              // TODO: replace with var(--cmd-warning-glow) once a warning-glow
              // token is defined (only --cmd-{accent,success,error}-glow exist
              // today, in apps/kiosk/index.html). The 'Return' button uses
              // var(--cmd-success-glow); this should mirror it for consistency.
              boxShadow: '0 4px 16px rgba(245, 158, 11, 0.25)',
              border: 'none',
              color: 'white',
            },
          }}
        >
          Return as defective
        </Button>
      </Group>

      <Button
        variant="default"
        size="md"
        onClick={reset}
        disabled={checkinMutation.isPending}
        styles={{
          root: {
            fontFamily: 'var(--font-heading)',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            backgroundColor: 'transparent',
            borderColor: 'var(--border-colour)',
            color: 'var(--text-secondary)',
          },
        }}
      >
        Cancel
      </Button>

      <FeedbackOverlay
        visible={overlay.visible}
        type={overlay.type}
        title={overlay.title}
        message={overlay.message}
        onHide={handleOverlayHide}
      />
    </Stack>
  );
}
